import React, { useEffect, useRef, useState } from 'react';
import { applicationProgressConfig } from '../UkMapLeaflet/UkMapSettings';
import './Hero.css';  // Import the CSS file that contains the fade-in animation

const HeroBarHorizontal = ({ approved = 0, inProgress = 0, target = 100, height = 60, showInProgress = true }) => {
  const containerRef = useRef();
  const [containerWidth, setContainerWidth] = useState(300);
  const [isTabletOrSmaller, setIsTabletOrSmaller] = useState(window.innerWidth <= 1024);
  const [showBar, setShowBar] = useState(false);

  useEffect(() => {
    if (approved > 0) {
      const timer = setTimeout(() => setShowBar(true), 500);
      return () => clearTimeout(timer);
    }
  }, [approved]);

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      if (entries[0]) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsTabletOrSmaller(window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const barHeight = height - 72;
  const boxHeight = isTabletOrSmaller ? 25 : 35;
  const boxWidth = isTabletOrSmaller ? 220 : 210;
  const fontSize = isTabletOrSmaller ? 16 : 20;
  const boxNumberFontSize = 25;
  const boxNumberPadding = 25;
  const boxNumberHeight = 74;
  const approvedWidth = Math.min((approved / target) * containerWidth, containerWidth);
  const inProgressWidth = Math.min((inProgress / target) * containerWidth, containerWidth - approvedWidth);
  const remainingWidth = Math.max(containerWidth - approvedWidth - inProgressWidth, 0);

  // Calculate callout positions with overflow handling
  const getCalloutPosition = (barPosition, barWidth, alignment) => {

    if (alignment === 'left') {
      // Left edge at the end of the bar if enough space
      const leftPosition = barPosition + barWidth + boxWidth / 2;
      if (leftPosition + boxWidth > containerWidth) {
        return boxWidth / 2;
      }
      return leftPosition;
    }

    if (alignment === 'right') {
      // Right edge at the end of the bar if enough space
      const rightPosition = barPosition + barWidth - boxWidth / 2;
      if (rightPosition - boxWidth < 0) {
        return containerWidth - boxWidth / 2;
      }
      return rightPosition;
    }

    throw new Error('Invalid alignment');

  };

  const dims = {
    approved: {
      width: approvedWidth,
      labelX: getCalloutPosition(0, approvedWidth, 'left'),
      labelY: 30
    },
    inProgress: {
      width: inProgressWidth,
      labelX: getCalloutPosition(approvedWidth, inProgressWidth, 'right'),
      calloutX: approvedWidth + inProgressWidth,
      calloutY: 30 + barHeight + 7,
      labelY: 30 + barHeight + 12 + boxHeight 
    },
    target: {
      width: remainingWidth + inProgressWidth + approvedWidth,
      labelX: getCalloutPosition(0, remainingWidth + inProgressWidth + approvedWidth, 'right'),
      labelY: 30,
      labelWidthAdjustment: 50
    },
    callout: {
      height: 6,
      width: 12
    }
  }

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <svg
        viewBox={`0 0 ${containerWidth} ${height}`}
        width="100%"
        height={height}
        className={`fade-in ${showBar ? "visible" : ""}`}
      >
        <defs>
          <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="1" stdDeviation="1" floodColor="#000" floodOpacity="0.2" />
          </filter>
        </defs>

        {/* Background bar */}
        <rect
          x={0}
          y={(height - barHeight) / 2}
          width={containerWidth}
          height={barHeight}
          fill="#fff"
          filter="url(#shadow)"
        />

        {/* Approved fill */}
        <rect
          x={0}
          y={(height - barHeight) / 2}
          width={dims.approved.width}
          height={barHeight}
          fill={applicationProgressConfig['Approved'].color}
        />

        {/* In-progress fill */}
        {showInProgress && (
          <rect
            x={dims.approved.width}
            y={(height - barHeight) / 2}
            width={dims.inProgress.width}
            height={barHeight}
            fill={applicationProgressConfig['In Progress'].color}
            rx={dims.approved.width === 0 ? 4 : 0}
          />
        )}

        {/* Right-end corner radius */}
        {remainingWidth === 0 && (
          <rect
            x={dims.approved.width + dims.inProgress.width - 4}
            y={(height - barHeight) / 2}
            width={4}
            height={barHeight}
            fill={applicationProgressConfig['In Progress'].color}
          />
        )}

        {/* Approved Label */}
        <rect
          x={dims.approved.labelX - boxWidth / 2}
          y={dims.approved.labelY - boxHeight}
          width={boxWidth}
          height={boxHeight}
          fill={applicationProgressConfig['Approved'].color}
          filter="url(#shadow)"
        />
        <polygon
          points={`
            ${dims.approved.width}, ${dims.approved.labelY} 
            ${dims.approved.width + dims.callout.width}, ${dims.approved.labelY} 
            ${dims.approved.width}, ${dims.approved.labelY + dims.callout.height}
          `}
          fill={applicationProgressConfig['Approved'].color}
        />
        <text
          x={dims.approved.labelX}
          y={dims.approved.labelY - boxHeight / 2 + (isTabletOrSmaller ? 4 : 10)}
          textAnchor="middle"
          fontSize={fontSize}
          fontWeight="bold"
          fill="#fff"
        >
          {isTabletOrSmaller ? `${approved} projects approved` : 'Projects approved'}
        </text>

        {/* In Progress Label */}
        {showInProgress && (
          <>
            <rect
              x={dims.inProgress.labelX - boxWidth / 2}
              y={dims.inProgress.labelY - boxHeight}
              width={boxWidth}
              height={boxHeight}
              fill={applicationProgressConfig['In Progress'].color}
              filter="url(#shadow)"
            />
            <polygon
              points={`
                ${dims.inProgress.calloutX}, ${dims.inProgress.calloutY + dims.callout.height}
                ${dims.inProgress.calloutX - dims.callout.width }, ${dims.inProgress.calloutY + dims.callout.height}
                ${dims.inProgress.calloutX}, ${dims.inProgress.calloutY}
              `}
              fill={applicationProgressConfig['In Progress'].color}
            />
            <text
              x={dims.inProgress.labelX}
              y={dims.inProgress.labelY - boxHeight / 2 + (isTabletOrSmaller ? 4 : 2)}
              textAnchor="middle"
              fontSize={fontSize}
              fontWeight="bold"
              fill="#fff"
            >
              {isTabletOrSmaller ? `${inProgress} projects in progress` : 'Projects in progress'}
            </text>
          </>
        )}

        {isTabletOrSmaller && 
          <text
            x={containerWidth - 5}
            y={height - 80}
            textAnchor="end"
            fontSize={fontSize - 4}
            fill="#000"
          >
            Target
          </text>
        }
        {!isTabletOrSmaller && 
          <rect
            x={dims.target.labelX - dims.target.labelWidthAdjustment}
            y={dims.target.labelY - boxHeight}
            width={boxWidth + dims.target.labelWidthAdjustment}
            height={boxHeight}
            fill={applicationProgressConfig['default'].color}
            filter="url(#shadow)"
          />
        }
        {!isTabletOrSmaller && 
          <polygon
            points={`
              ${dims.target.width}, ${dims.target.labelY} 
              ${dims.target.width - dims.callout.width}, ${dims.target.labelY} 
              ${dims.target.width}, ${dims.target.labelY + dims.callout.height}
            `}
            fill={applicationProgressConfig['default'].color}
          />
        }
        {!isTabletOrSmaller && 
          <text
            x={dims.target.labelX + dims.target.labelWidthAdjustment / 2}
            y={dims.target.labelY - boxHeight / 2 + (isTabletOrSmaller ? 4 : 10)}
            textAnchor="middle"
            fontSize={fontSize}
            fontWeight="bold"
            fill="#fff"
          >
            Target
          </text>
        }
        {!isTabletOrSmaller && (
          <>
            <text
              x={dims.approved.width - boxNumberPadding}
              y={boxNumberHeight}
              textAnchor="middle"
              fontSize={boxNumberFontSize}
              fontWeight="bold"
              fill="#fff"
            >{approved}</text>
            {showInProgress && (
              <text
                x={dims.inProgress.calloutX - boxNumberPadding}
                y={boxNumberHeight}
                textAnchor="middle"
                fontSize={boxNumberFontSize}
                fontWeight="bold"
                fill="#fff"
              >{inProgress}</text>
            )}
          </>
        )}
        <text
          x={dims.target.width - boxNumberPadding}
          y={boxNumberHeight}
          textAnchor="middle"
          fontSize={boxNumberFontSize}
          fontWeight="bold"
          fill="#000"
        >{target}</text>
      </svg>
    </div>
  );
};

export default HeroBarHorizontal;
